<template>
  <v-container fluid grid-list-sm>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          <span class="font-weight-medium">
            {{ $t('EREPORTER.INVOICES.ITEM') }}
          </span>
        </v-toolbar-title>

        <v-spacer />

        <v-btn dark color="indigo" @click="$router.go(-1)">
          {{ $t('BUTTON.BACK') }}
        </v-btn>

        <v-btn
          class="ml-2"
          color="primary"
          @click="
            $router.push({
              name: 'ereporter.invoice.edit',
              params: { id: id }
            })
          "
        >
          {{ $t('BUTTON.EDIT') }}
        </v-btn>
      </v-toolbar>
      <v-divider />
    </v-card>
    <v-card class="mt-3" :loading="loading">
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <invoice-form
              v-if="invoice.id"
              v-model="invoice"
              :address="invoice.address"
              :customer="customer"
              :customer-type="invoice.customer_type"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <invoice-line-list
              class="mt-5 pa-5"
              :lines="invoice.lines"
              :total-price="invoice.price"
              :sort-by="['default']"
              :type="getListTypeByInvoiceType(invoice.type_id)"
            >
              <template #actions="{item}">
                <v-list-item v-if="item.entity_id" @click="show(item)">
                  <v-list-item-title>
                    <v-icon class="mr-1" small>mdi-eye</v-icon>
                    {{ $t('ITEM.VIEW') }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </invoice-line-list>
            <v-dialog
              v-if="dialog"
              v-model="dialog"
              persistent
              width="unset"
              max-width="1400"
            >
              <component
                :is="component.name"
                v-bind="component.data"
                @updated="cancelDialogAndRefresh"
                @cancel="cancelDialog"
              />
            </v-dialog>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="3">
            <export-language-select @getPdf="getPdf" />
          </v-col>
          <v-tooltip :disabled="!sendViaEmailDisabled()" top>
            <template v-slot:activator="{ on }">
              <div class="mt-5" v-on="on">
                <v-btn
                  class="ml-2"
                  :disabled="sendViaEmailDisabled()"
                  @click="popupSendViaEmail()"
                >
                  {{ $t('BUTTON.SEND_EMAIL') }}
                </v-btn>
              </div>
            </template>
            <span v-if="validStatusForSendViaEmail()">{{
              $t('EREPORTER.TOOLTIP_INFO_EMAIL')
            }}</span>
            <span v-else>{{ $t('EREPORTER.TOOLTIP_INFO_STATUS') }}</span>
          </v-tooltip>
          <v-spacer />
        </v-row>
        <confirm-dialog ref="confirm" />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import downloadFile from '@/modules/_global/mixins/download-file';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import InvoiceForm from '../../components/forms/Invoices/InvoiceForm';
import InvoiceLineList from '../../components/Invoices/InvoiceLineList';
import ExportLanguageSelect from '@/modules/ereporter/components/ExportLanguageSelect';
import { CUSTOMER } from '@/modules/ereporter/common/Invoices/customerTypes';
import { FINISHED } from '@/modules/plannings/common/event-statuses';
import {
  SENT_TO_ABACUS,
  PRINTED,
  SENT_VIA_EMAIL
} from '@/modules/ereporter/common/Invoices/statuses';
import {
  EVENT,
  SUBSCRIPTION,
  CONTRACTS
} from '@/modules/ereporter/common/Invoices/lineEntities';
import {
  C,
  BASE
} from '@/modules/ereporter/components/Invoices/InvoiceLineList.vue';
import { CONTRACT } from '@/modules/ereporter/common/Invoices/types';

export default {
  name: 'InvoicesShowPage',

  components: {
    ExportLanguageSelect,
    InvoiceForm,
    InvoiceLineList,
    ViewEventTask: () =>
      import('@/modules/plannings/components/Event/ViewEventTask'),
    EditSubscription: () =>
      import('@/modules/subscriptions/components/EditSubscription'),
    ShowInvoiceContract: () =>
      import(
        '@/modules/building_complexes_contracts/components/ShowInvoiceContract'
      ),
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog')
  },

  mixins: [i18nRefreshData, downloadFile],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    invoice: {
      lines: []
    },
    customer: {},
    invoiceLoaded: false,
    dialog: false,
    error: null,
    component: null
  }),

  computed: {
    ...mapGetters(['loading']),
    ...mapState('ereporter', ['currentInvoice']),

    isLastLine() {
      return this.invoice.lines.length === 1;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('ereporter', [
      'getInvoice',
      'getInvoicePdf',
      'sendInvoicesViaEmails'
    ]),

    refreshData() {
      this.getInvoice({ invoiceId: this.id }).then(({ data }) => {
        this.invoice = data.data;
        this.customer = data.data.customer;
      });
    },

    show(item) {
      this.component = {
        name: this.getComponentNameByEntity(item.entity_type),
        data: {
          id: item.entity_id
        }
      };
      this.dialog = true;
    },

    getListTypeByInvoiceType(type) {
      switch (type) {
        case CONTRACT:
          return C;

        default:
          return BASE;
      }
    },

    getComponentNameByEntity(entity) {
      switch (entity) {
        case EVENT:
          return 'ViewEventTask';
        case SUBSCRIPTION:
          return 'EditSubscription';
        case CONTRACTS:
          return 'ShowInvoiceContract';

        default:
          throw new Error('Undefined entity!');
      }
    },

    cancelDialog() {
      this.dialog = false;
      this.component = null;
    },

    cancelDialogAndRefresh(event) {
      if (this.isLastLine && event.status_id !== FINISHED) {
        this.$router.push({ name: 'invoices' });
        return;
      }

      this.cancelDialog();
      this.refreshData();
    },

    getPdf(lang) {
      this.getInvoicePdf({
        id: this.id,
        params: { language: lang.id }
      })
        .then(response => {
          this.downloadFile(response);
        })
        .catch(({ response }) => {
          console.log(response);
        });
    },

    validStatusForSendViaEmail() {
      let valid_statuses = [SENT_TO_ABACUS, PRINTED, SENT_VIA_EMAIL];

      return valid_statuses.includes(this.invoice.status_id);
    },

    sendViaEmailDisabled() {
      return !(
        this.validStatusForSendViaEmail() && this.customer.invoice_email
      );
    },

    popupSendViaEmail() {
      this.$refs.confirm
        .open(this.$t('EREPORTER.INVOICES.SEND_EMAIL_QUESTION'), null, {
          cancelBtnText: this.$t('EREPORTER.SEND_EMAIL_QUESTION_CANCEL_BUTTON'),
          agreeBtnText: this.$t('EREPORTER.SEND_EMAIL_QUESTION_AGREE_BUTTON')
        })
        .then(confirm => {
          if (confirm) {
            this.sendInvoicesViaEmails({ ids: [this.id] })
              .then(() => {
                this.refreshData();
              })
              .catch(({ response }) => {
                this.handleErrorsByResponse(response);
              });
          }
        });
    }
  }
};
</script>

<style scoped></style>
